import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchUserNotes } from '../utils/noteFetchUtils'; // Import the utility
import useNotesPolling from '../utils/useNotesPolling';
import { getAutomaticNoteType } from '../utils/automaticNoteType';
import { useNote } from '../context/NoteContext'; // To update the note context
import { formatTimestampForUser, getRelativeTime } from '../utils/timezoneUtils'; // Import timezone utility
import { getDocumentType, getDocumentLabel, addDocumentNumbering, exportDocument, exportAllSessionDocuments } from '../utils/docViewUtils'; // Import new utilities
import '../css/TopLeftNoteViewer.css';
import { FaSync, FaSpinner, FaChevronDown, FaFileDownload } from 'react-icons/fa'; // Import icons

function NoteViewer({ authenticated = true }) {
  const { t } = useTranslation();
 const { allNotes, setAllNotes, loadingNotes, removeLoadingNote, removeLoadingNoteBySessionId, syncLoadingStateFromSessionNotes, lastRecordingId, setNoteWithSession, isRecording, isRecordingActive } = useNote(); // Get the context and loading state
 const [isRefreshing, setIsRefreshing] = useState(false);
 const isRefreshingRef = useRef(false);
 const manualRequestRef = useRef(null);
 const processedNoteIds = useRef(new Set()); // Track which notes we've already processed
 const [expandedSession, setExpandedSession] = useState(null); // Track which session dropdown is open

 // Reloads and other tabs have no local pending state. Keep one baseline
 // subscription while authenticated, independently of local receipt tracking.

 useEffect(() => {
   const cancelManual = () => {
     manualRequestRef.current?.abort();
     setIsRefreshing(false);
   };
   document.addEventListener('visibilitychange', cancelManual);
   return () => {
     manualRequestRef.current?.abort();
     document.removeEventListener('visibilitychange', cancelManual);
   };
 }, [lastRecordingId, authenticated]);

 // Close dropdown when clicking outside
 useEffect(() => {
   const handleClickOutside = () => {
     setExpandedSession(null);
   };

   if (expandedSession) {
     document.addEventListener('click', handleClickOutside);
     return () => document.removeEventListener('click', handleClickOutside);
   }
 }, [expandedSession]);

const fetchAndSetNotes = useCallback(async ({ showSpinner = false, signal } = {}) => {
  if (isRefreshingRef.current && !isRefreshingRef.current.signal?.aborted) return;

  const request = { signal };
  isRefreshingRef.current = request;
  if (showSpinner) {
    setIsRefreshing(true);
  }
  try {
    const fetchedNotes = await fetchUserNotes({ signal }); // Fetch the notes from the server
    if (signal?.aborted) return;
    setAllNotes(fetchedNotes); // Update the shared note cache
    // version is tracked by backend long-poll response, not by client-created_at scan.

    // After fetching, check if any of the new notes belong to our last recording session.
    if (lastRecordingId) {
      const newNotesForSession = fetchedNotes.filter(note =>
        // Find notes for the right session that we haven't already processed
        note.session_id === lastRecordingId && !processedNoteIds.current.has(note.id)
      );

      if (newNotesForSession.length > 0) {
        // If we found new notes, decide which one to show automatically.
        // We will prioritize showing the "Patient Note" if it has arrived.
        const patientNote = newNotesForSession.find(n => getAutomaticNoteType(n) === 'Patient Note');
        const transcriptNote = newNotesForSession.find(n => getAutomaticNoteType(n) === 'Transcript');

        // Use the Patient Note if available, otherwise the Transcript, or fall back to the first new note.
        const noteToShow = patientNote || transcriptNote || newNotesForSession[0];

        if (noteToShow) {
          console.log('Automatically displaying newly arrived note in TextViewer.');
          // This updates the shared context, which tells TextViewer to display the new note.
          setNoteWithSession(noteToShow.note, noteToShow.session_id);
        }
      }
    }
    // This part handles removing the "Processing..." indicators from the list.
    if (fetchedNotes && fetchedNotes.length > 0) {
      const notesBySession = {};
      fetchedNotes.forEach((note) => {
        if (!note.session_id) return;
        if (!notesBySession[note.session_id]) notesBySession[note.session_id] = [];
        notesBySession[note.session_id].push(note);
      });
      Object.entries(notesBySession).forEach(([sessionId, sessionNotes]) => {
        syncLoadingStateFromSessionNotes(sessionId, sessionNotes);
      });

      fetchedNotes.forEach(note => {
        // Skip notes we've already processed
        if (processedNoteIds.current.has(note.id)) return;

        const type = getAutomaticNoteType(note);

        if (type && note.session_id) {
          removeLoadingNoteBySessionId(note.session_id, type);
        } else if (type) {
          removeLoadingNote(type);
        }

        // Mark this note as processed so we don't handle it again
        processedNoteIds.current.add(note.id);
      });
    }
  } catch (error) {
    if (!signal?.aborted) console.error('Failed to fetch notes:', error);
  } finally {
    if (isRefreshingRef.current === request) isRefreshingRef.current = false;
    if (showSpinner && !signal?.aborted) {
      setIsRefreshing(false);
    }
  }
}, [
  lastRecordingId,
  removeLoadingNote,
  removeLoadingNoteBySessionId,
  setAllNotes,
  setNoteWithSession,
  syncLoadingStateFromSessionNotes,
]);

 useNotesPolling(fetchAndSetNotes, authenticated);

 // Function to handle clicking on a session row
 const handleSessionClick = (sessionData) => {
   if (sessionData.isLoading) return; // Don't do anything if it's a loading session

   // Find the patient note for this session (prioritize it over transcript)
   const patientNote = sessionData.notes.find(note => getDocumentType(note) === 'Patient Note');
   const noteToShow = patientNote || sessionData.notes[0]; // Fallback to first note if no patient note

   if (noteToShow) {
     setNoteWithSession(noteToShow.note, sessionData.sessionId); // Show the patient note first with session context
   }
 };

 // Function to handle export dropdown toggle
 const handleExportToggle = (sessionId, event) => {
   event.stopPropagation(); // Prevent session click
   setExpandedSession(expandedSession === sessionId ? null : sessionId);
 };

 // Function to export specific document
 const handleExportDocument = (note, event) => {
   event.stopPropagation(); // Prevent session click
   exportDocument(note, formatTimestampForUser);
   setExpandedSession(null); // Close dropdown
 };

 // Function to export all documents in a session
 const handleExportAllSession = (sessionData, event) => {
   event.stopPropagation(); // Prevent session click
   exportAllSessionDocuments(sessionData, formatTimestampForUser);
   setExpandedSession(null); // Close dropdown
 };

 // Group notes by session_id and combine with loading notes
 const groupedSessions = () => {
   // Group real notes by session_id
   const sessionGroups = {};

   allNotes.forEach(note => {
     const sessionId = note.session_id || 'no-session';
     if (!sessionGroups[sessionId]) {
       sessionGroups[sessionId] = {
         sessionId,
         notes: [],
         latestTimestamp: note.created_at,
         isLoading: false,
         pendingTypes: []
       };
     }
     sessionGroups[sessionId].notes.push(note);

     // Update latest timestamp if this note is newer
     if (new Date(note.created_at) > new Date(sessionGroups[sessionId].latestTimestamp)) {
       sessionGroups[sessionId].latestTimestamp = note.created_at;
     }
   });

   // Add numbering to each session's notes
   Object.keys(sessionGroups).forEach(sessionId => {
     sessionGroups[sessionId].notes = addDocumentNumbering(sessionGroups[sessionId].notes);
   });

   // Incorporate loading state:
   // - If there are no real notes yet for a session, show a processing row (existing behavior)
   // - If a Transcript has arrived but Patient Note hasn't, surface a subtle waiting indicator
   loadingNotes.forEach(loadingNote => {
     const sessionId = loadingNote.sessionId || loadingNote.recordingId || 'loading';

     if (sessionGroups[sessionId] && sessionGroups[sessionId].notes.length > 0) {
       // We have some real notes for this session; compute pending types based on arrived notes
       const typesPresent = new Set(
         sessionGroups[sessionId].notes.map(getAutomaticNoteType)
       );
       const pending = [];
       if (typesPresent.has('Transcript') && !typesPresent.has('Patient Note')) {
         pending.push('Patient Note');
       }
       sessionGroups[sessionId].pendingTypes = pending;
       return;
     }

     // No real notes yet; keep showing the processing placeholder
     if (!sessionGroups[sessionId]) {
       sessionGroups[sessionId] = {
         sessionId,
         notes: [],
         latestTimestamp: loadingNote.timestamp,
         isLoading: true,
         pendingTypes: []
       };
     }
     sessionGroups[sessionId].notes.push({
       id: loadingNote.id,
       sessionId: loadingNote.sessionId,
       recordingId: loadingNote.recordingId,
       type: loadingNote.type,
       created_at: loadingNote.timestamp,
       isLoading: true
     });
   });

   // Convert to array and sort by latest timestamp
   return Object.values(sessionGroups).sort((a, b) => {
     return new Date(b.latestTimestamp) - new Date(a.latestTimestamp);
   });
 };

 const sessionData = groupedSessions();

 // Manual refresh handler
 const handleManualRefresh = () => {
   manualRequestRef.current?.abort();
   manualRequestRef.current = new AbortController();
   fetchAndSetNotes({ showSpinner: true, signal: manualRequestRef.current.signal });
 };

 return (
   <div className="top-left-note-viewer-container">
     <div className="top-left-header">
       <h2>{t('notes.documents')}</h2>

       {/* Refresh icon with tooltip */}
       <div className="refresh-icon-container" onClick={handleManualRefresh}>
         <FaSync className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`} />
         <span className="tooltip-text">{t('notes.refresh')}</span>
       </div>
     </div>

     <div className="notes-container">
       <table>
         <thead>
           <tr>
             <th>{t('notes.session')}</th>
            <th>{t('notes.created')}</th>
             <th>{t('notes.export')}</th>
           </tr>
         </thead>
         <tbody>
           {isRecording && (
             <tr className="taking-notes-row">
               <td>
                 <div className="taking-notes-info">
                   <img
                     src="/extended-video-unscreen.gif"
                     alt={t('notes.taking')}
                     className={`recording-gif ${!isRecordingActive ? 'paused' : ''}`}
                   />
                   <span>{t('notes.takingProgress')}</span>
                 </div>
               </td>
               <td>
                 <span className={`live-indicator ${!isRecordingActive ? 'inactive' : ''}`}>
                   {isRecordingActive ? t('notes.live') : t('notes.paused')}
                 </span>
               </td>
               <td>
                 {/* Empty export column during recording */}
               </td>
             </tr>
           )}
           {sessionData.map((session, index) => (
             <tr
               key={session.sessionId || index}
               onClick={() => handleSessionClick(session)}
               className={`session-row ${session.isLoading ? 'loading-row' : ''}`}
             >
               <td>
                 {session.isLoading ? (
                   <div className="session-loading">
                     <FaSpinner className="spinning" />
                     <span>{t('notes.processingSession')}</span>
                   </div>
                 ) : (
                   <div className="session-info">
                     <div className="session-title">
                       {t('notes.sessionCount', { count: session.notes.length })}
                     </div>
                     <div className="session-docs">
                       {session.notes.map(note => note.type).join(', ')}
                     </div>
                     {session.pendingTypes && session.pendingTypes.length > 0 && (
                       <div className="waiting-indicator">
                         <FaSpinner className="spinning" />
                         <span>{t('notes.waiting', { document: session.pendingTypes.map(getDocumentLabel).join(', ') })}</span>
                       </div>
                     )}
                   </div>
                 )}
               </td>
               <td>
                 {session.isLoading ? (
                   <div className="loading-spinner">
                     <FaSpinner className="spinning" />
                     <span>{t('common.processing')}</span>
                   </div>
                ) : (
                  getRelativeTime(session.latestTimestamp)
                )}
              </td>
               <td>
                 {!session.isLoading && (
                   <div className="export-dropdown">
                     <button
                       className="export-button"
                       onClick={(e) => handleExportToggle(session.sessionId, e)}
                     >
                       <FaFileDownload />
                       <FaChevronDown className={`chevron ${expandedSession === session.sessionId ? 'expanded' : ''}`} />
                     </button>
                     {expandedSession === session.sessionId && (
                       <div className="dropdown-menu">
                         <button
                           className="dropdown-item"
                           onClick={(e) => handleExportAllSession(session, e)}
                         >{t('notes.exportAll')}</button>
                         {session.notes.map((note, noteIndex) => (
                           <button
                             key={noteIndex}
                             className="dropdown-item"
                             onClick={(e) => handleExportDocument(note, e)}
                           >
                             {t('notes.exportDocument', { document: note.type })}
                           </button>
                         ))}
                       </div>
                     )}
                   </div>
                 )}
               </td>
             </tr>
           ))}
         </tbody>
       </table>
     </div>
   </div>
 );
}

export default NoteViewer;
